import { ComponentType } from 'react';

import { Box, Typography } from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';

import {
  SidebarValue,
  SidebarValueWrapper,
  StatusError,
  StatusOK,
} from '@spotify/confidence-core-react';
import { MaterializedSegmentLoadJobFragment } from '@spotify/confidence-graphql';
import { SidebarSectionWithCommentZones } from '@spotify/plugin-confidence-comments-react';

import {
  formatBytes,
  formatFalsePositiveRate,
} from '../../../utils/loadJobUtils';

const BLOOM_FILTER_SIZE_LIMIT_BYTES = 512 * 1024;

type BloomFilterDetails = NonNullable<
  NonNullable<
    MaterializedSegmentLoadJobFragment['bloomFilterStatus']
  >['details']
>;

export type LocalResolverSectionProps = {
  bloomFilterDetails?: BloomFilterDetails;
};

export type LocalResolverSectionComponent =
  ComponentType<LocalResolverSectionProps>;

const BloomFilterContent = ({
  bloomFilterDetails,
}: {
  bloomFilterDetails: BloomFilterDetails;
}) => {
  const sizeBytes = Number(bloomFilterDetails.sizeBytes);
  const exceedsLimit = sizeBytes >= BLOOM_FILTER_SIZE_LIMIT_BYTES;

  return (
    <>
      <SidebarValue
        name="Status"
        value={
          exceedsLimit ? (
            <Box
              display="flex"
              alignItems="center"
              style={{ gap: 'var(--encore-spacing-tighter-2)' }}
            >
              <StatusError />
              <span>Excluded from resolver state — too large</span>
            </Box>
          ) : (
            <Box
              display="flex"
              alignItems="center"
              style={{ gap: 'var(--encore-spacing-tighter-2)' }}
            >
              <StatusOK />
              <span>Included in resolver state</span>
            </Box>
          )
        }
      />
      {exceedsLimit && (
        <Alert severity="error">
          <AlertTitle>Bloom filter not delivered to local resolvers</AlertTitle>
          This bloom filter is {formatBytes(sizeBytes)}, which exceeds the 512
          KB limit for local resolver state delivery. This segment will still
          work with online resolver SDKs (using the Confidence backend) and with
          local resolver SDKs configured with a remote materialization store or
          a customer-hosted materialization store.
        </Alert>
      )}
      <SidebarValue
        name="Keys"
        value={Number(bloomFilterDetails.keyCount).toLocaleString()}
      />
      <SidebarValue name="Size" value={formatBytes(sizeBytes)} />
      <SidebarValue
        name="False positive rate"
        value={`${formatFalsePositiveRate(
          bloomFilterDetails.falsePositiveRate,
        )} entities risk being incorrectly targeted`}
      />
    </>
  );
};

export const LocalResolverSdkSupport = ({
  bloomFilterDetails,
}: LocalResolverSectionProps) => (
  <SidebarSectionWithCommentZones
    title="Local resolver SDK support"
    description={
      <>
        <p>
          Local resolvers check segment membership using a bloom filter,
          avoiding a network round-trip.
        </p>
        <p>
          A bloom filter is a compact probabilistic data structure: it can say
          with certainty that an entity is not in a segment, or that it probably
          is, subject to a small false-positive rate.
        </p>
        <Typography component="p" variant="body2" color="textSecondary">
          Supported in Java ≥0.15.2, JS ≥0.15.2, Python ≥0.8.0, Go ≥0.18.0, Rust
          ≥0.6.1, Ruby ≥0.1.4, and Cloudflare ≥0.10.6.
        </Typography>
      </>
    }
  >
    <SidebarValueWrapper>
      {bloomFilterDetails ? (
        <BloomFilterContent bloomFilterDetails={bloomFilterDetails} />
      ) : (
        <Typography variant="body2" color="textSecondary">
          No bloom filter available. This segment requires a round-trip to
          resolve in local resolver SDKs.
        </Typography>
      )}
    </SidebarValueWrapper>
  </SidebarSectionWithCommentZones>
);
